import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonTable } from '../../components/Skeleton';
import { AUDIT_ACTIONS, type AuditFilters, downloadAuditCsv } from './api';
import { useFileDownload } from '../tickets/articles/attachmentFile';
import { AuditDetail } from './AuditDetail';
import { AuditSummaryText } from './presentation';
import { useModuleLabel } from './useModuleLabel';
import { useAuditActors, useAuditEntries, useAuditModules } from './queries';
import { Select } from '../../components/Select';
import { MultiCombobox } from '../../components/MultiCombobox';

/**
 * Die Auditansicht -- F5-02.
 *
 * **Warum es sie gibt, obwohl das Protokoll seit Migration 0001 laeuft.** Der
 * Nachweis lag bisher in der Datenbank. Ein Nachweis, den man nur mit einem
 * Datenbankwerkzeug lesen kann, ist im Zweifel keiner: Bei einer Pruefung
 * sitzt der Pruefer nicht an der Konsole.
 *
 * **Kein rohes JSON in der Liste.** Jede Zeile traegt einen Satz -- gebaut
 * aus Vorlage und Werten, die der Server liefert (siehe `presentation.tsx`).
 * Die vollstaendigen Aenderungen stehen in der Detailansicht, und das ist
 * kein Platzmangel: Eine Liste, die alte und neue Werte offen zeigt, ist ein
 * Personenverzeichnis.
 *
 * **Die Filter sind fest und nicht frei.** Datum, Bearbeiter, Modul, Aktion.
 * Ein freier Ausdruck ueber die Aenderungen waere eine Suche nach alten und
 * neuen Werten quer durch den Bestand -- ein Werkzeug, mit dem sich das
 * Protokoll nach Personen durchsuchen liesse, ohne dass es auffaellt.
 *
 * **Es gibt nichts zu aendern und nichts zu loeschen.** `audit_log` ist
 * nachtraeglich nicht aenderbar; die Anwendungsrolle hat weder UPDATE noch
 * DELETE. Diese Seite hat deshalb keinen einzigen Knopf, der schreibt.
 */

/** Die vordefinierten Zeitraeume ueber den Datumsfeldern. */
const RANGES = ['today', 'week', 'month', 'all'] as const;
type Range = (typeof RANGES)[number];

const ACTION_TONES: Record<string, BadgeTone> = {
  create: 'success',
  update: 'info',
  delete: 'danger',
  login_failed: 'danger',
  anonymize: 'warning',
  export: 'warning',
  read_sensitive: 'warning',
};

function isoDay(offsetDays: number): string {
  const day = new Date();
  day.setDate(day.getDate() - offsetDays);
  return day.toISOString().slice(0, 10);
}

function rangeToDates(range: Range): { from?: string; to?: string } {
  switch (range) {
    case 'today':
      return { from: isoDay(0), to: isoDay(0) };
    case 'week':
      return { from: isoDay(6), to: isoDay(0) };
    case 'month':
      return { from: isoDay(29), to: isoDay(0) };
    case 'all':
      return {};
  }
}

export function AuditPage({ objectId }: { objectId?: string } = {}) {
  const { t, i18n } = useTranslation();
  const moduleLabel = useModuleLabel();

  const [range, setRange] = useState<Range>('week');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [modules, setModules] = useState<string[]>([]);
  const [actors, setActors] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [openEntry, setOpenEntry] = useState<string | null>(null);

  const catalogue = useAuditModules();
  const people = useAuditActors();

  // Die Auswahl steht auf **Modulen**, der Server filtert nach
  // **Objektarten**. Ein Modul umfasst mehrere Arten (`tickets` umfasst
  // `ticket`, `ticket_article`, `attachment` ...). Die Umrechnung gehoert
  // hierhin: Der Betrachter denkt in Modulen, das Datenmodell kennt Arten.
  const objectTypes = useMemo(() => {
    if (modules.length === 0) return undefined;
    const chosen = (catalogue.data ?? []).filter((entry) => modules.includes(entry.key));
    return chosen.flatMap((entry) => entry.object_types);
  }, [modules, catalogue.data]);

  const dates = range === 'all' && (from || to) ? { from, to } : rangeToDates(range);

  const filters: AuditFilters = {
    ...(objectTypes && objectTypes.length > 0 ? { object_type: objectTypes } : {}),
    ...(actors.length > 0 ? { actor_user_id: actors } : {}),
    ...(actions.length > 0 ? { action: actions } : {}),
    ...(objectId ? { object_id: objectId } : {}),
    ...(dates.from ? { from: dates.from } : {}),
    ...(dates.to ? { to: dates.to } : {}),
  };

  const page = useAuditEntries(filters, offset);
  const exportFile = useFileDownload();

  // Jede Filteraenderung beginnt wieder auf Seite eins. Ohne das stuende man
  // nach dem Einschraenken auf Seite drei einer Liste, die nur noch zwei hat
  // -- und sie waere leer, ohne dass es dafuer einen Grund gaebe.
  const change = <T,>(setter: (value: T) => void) => {
    return (value: T) => {
      setter(value);
      setOffset(0);
    };
  };

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  const reset = () => {
    setRange('week');
    setFrom('');
    setTo('');
    setModules([]);
    setActors([]);
    setActions([]);
    setOffset(0);
  };

  const filtered =
    modules.length > 0 || actors.length > 0 || actions.length > 0 || range !== 'week';

  return (
    <>
      <PageHeader
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
        action={
          // Die einzige Ausgabe des Protokolls -- sie lag bis dahin im Reiter
          // "Protokoll" der Datenschutzseite, den es nicht mehr gibt. Sie
          // nimmt dieselben Filter wie die Liste.
          <Button
            variant="secondary"
            busy={exportFile.isPending}
            onClick={() => {
              exportFile.mutate({
                fetchFile: async () => ({ blob: await downloadAuditCsv(filters), filename: null }),
                fallbackName: 'protokoll.csv',
              });
            }}
          >
            {t('audit.export')}
          </Button>
        }
      />
      {exportFile.isError ? <ErrorNotice error={exportFile.error} /> : null}

      <section className="card">
        <div className="row">
          <div className="field">
            <label className="field__label" htmlFor="audit-range">
              {t('audit.period')}
            </label>
            <Select
              id="audit-range"
              className="field__control"
              value={range}
              onChange={(event) => {
                change(setRange)(event.target.value as Range);
              }}
            >
              {RANGES.map((value) => (
                <option key={value} value={value}>
                  {t(`audit.range.${value}`)}
                </option>
              ))}
            </Select>
          </div>

          {/* Von und Bis nur bei "gesamter Zeitraum": Zwei Datumsfelder neben
              einer Auswahl, die sie ueberschreibt, sind zwei Bedienelemente
              fuer eine Entscheidung -- und eines davon wirkt nicht. */}
          {range === 'all' ? (
            <>
              <div className="field">
                <label className="field__label" htmlFor="audit-from">
                  {t('time.from')}
                </label>
                <input
                  id="audit-from"
                  type="date"
                  className="field__control"
                  value={from}
                  onChange={(event) => {
                    change(setFrom)(event.target.value);
                  }}
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="audit-to">
                  {t('time.to')}
                </label>
                <input
                  id="audit-to"
                  type="date"
                  className="field__control"
                  value={to}
                  onChange={(event) => {
                    change(setTo)(event.target.value);
                  }}
                />
              </div>
            </>
          ) : null}
        </div>

        <details className="stack">
          <summary className="table__subject">{t('audit.moreFilters')}</summary>

          <fieldset className="field">
            <legend className="field__label">{t('audit.module')}</legend>
            {catalogue.isError ? <ErrorNotice error={catalogue.error} /> : null}
            <div className="row">
              {(catalogue.data ?? []).map((entry) => (
                <label className="field__checkbox" key={entry.key}>
                  <input
                    type="checkbox"
                    checked={modules.includes(entry.key)}
                    onChange={() => {
                      change(setModules)(toggle(modules, entry.key));
                    }}
                  />
                  {moduleLabel(entry.key)}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="field">
            <legend className="field__label">{t('audit.actor')}</legend>
            {people.isError ? <ErrorNotice error={people.error} /> : null}
            {/* Eine Liste mit Kaestchen je Bearbeiter wuchs mit jedem Zugang;
                gesucht wird jetzt, gewaehlt als Marke (F1-49). */}
            <MultiCombobox
              ariaLabel={t('audit.actor')}
              value={actors}
              options={(people.data ?? []).map((person) => ({ id: person.id, label: person.name }))}
              onChange={change(setActors)}
            />
          </fieldset>

          <fieldset className="field">
            <legend className="field__label">{t('audit.action')}</legend>
            <div className="row">
              {AUDIT_ACTIONS.map((value) => (
                <label className="field__checkbox" key={value}>
                  <input
                    type="checkbox"
                    checked={actions.includes(value)}
                    onChange={() => {
                      change(setActions)(toggle(actions, value));
                    }}
                  />
                  {t(`history.action.${value}`, { defaultValue: value })}
                </label>
              ))}
            </div>
          </fieldset>
        </details>

        {filtered ? (
          <Button variant="secondary" onClick={reset}>
            {t('audit.resetFilters')}
          </Button>
        ) : null}
      </section>

      <section className="card card--flush">
        {page.isPending ? <SkeletonTable columns={5} /> : null}
        {page.isError ? <ErrorNotice error={page.error} /> : null}

        {page.data?.items.length === 0 ? (
          <EmptyState
            icon="audit"
            title={t('audit.noEntries')}
            hint={filtered ? t('audit.noEntriesFiltered') : t('audit.noEntriesHint')}
            action={
              filtered ? (
                <Button variant="secondary" onClick={reset}>
                  {t('audit.resetFilters')}
                </Button>
              ) : undefined
            }
          />
        ) : null}

        {page.data && page.data.items.length > 0 ? (
          <div className="table-wrapper table-wrapper--tall">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('audit.occurredAt')}</th>
                  <th scope="col">{t('audit.summary')}</th>
                  <th scope="col">{t('audit.actor')}</th>
                  <th scope="col">{t('audit.module')}</th>
                  <th scope="col">
                    <span className="visually-hidden">{t('audit.details')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {page.data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="table__muted">
                      {new Date(entry.occurred_at).toLocaleString(i18n.language)}
                    </td>
                    <td>
                      <span className="table__subject">
                        {entry.object_label ? `${entry.object_label}: ` : ''}
                        <AuditSummaryText summary={entry.summary} />
                      </span>{' '}
                      <Badge tone={ACTION_TONES[entry.action] ?? 'neutral'}>
                        {t(`history.action.${entry.action}`, { defaultValue: entry.action })}
                      </Badge>
                    </td>
                    <td className="table__muted">
                      {entry.actor_name ??
                        t(`history.actor.${entry.actor_type}`, {
                          defaultValue: entry.actor_type,
                        })}
                    </td>
                    <td className="table__muted">{moduleLabel(entry.module)}</td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setOpenEntry(entry.id);
                        }}
                      >
                        {t('audit.details')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Vor und zurueck statt Seitenzahlen: Der Server sagt, ob es
            weitergeht, aber nicht, wie viele Seiten es gibt -- ein
            ``count(*)`` ueber zehntausend Zeilen je Aufruf waere teurer als
            die Auskunft wert ist. */}
        {page.data && (page.data.has_more || offset > 0) ? (
          <div className="row">
            <Button
              variant="secondary"
              disabled={offset === 0}
              onClick={() => {
                setOffset(Math.max(0, offset - 50));
              }}
            >
              {t('audit.previousPage')}
            </Button>
            <Button
              variant="secondary"
              disabled={!page.data.has_more}
              onClick={() => {
                setOffset(page.data.next_offset ?? offset);
              }}
            >
              {t('audit.nextPage')}
            </Button>
          </div>
        ) : null}
      </section>

      {openEntry ? (
        <AuditDetail
          entryId={openEntry}
          onClose={() => {
            setOpenEntry(null);
          }}
        />
      ) : null}
    </>
  );
}
